import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { environment } from "@env/environment";
import { Observable, throwError } from "rxjs";
import { catchError, map, shareReplay } from "rxjs/operators";
import {
    CreateOrganizationResponseSchema,
    GetOrganizationByIdResponseSchema,
    OrganizationArraySchema,
    SearchOrganizationsResponseSchema,
    SuggestDomainResponseSchema,
    UpdateOrganizationResponseSchema,
} from "./organization.schemas";
import {
    CreateOrganizationRequest,
    CreateOrganizationResponse,
    GetOrganizationByIdResponse,
    MigrateOrganizationUsersRequest,
    Organization,
    SearchOrganizationsRequest,
    SearchOrganizationsResponse,
    SuggestDomainResponse,
    UpdateOrganizationRequest,
    UpdateOrganizationResponse,
} from "./organization.types";

/**
 * Service for organization HTTP operations
 * Uses Observables for all async operations with runtime validation via zod
 */
@Injectable({
    providedIn: "root",
})
export class OrganizationService {
    /**
     * Cache for suggestDomain requests keyed by normalized email domain
     */
    private readonly suggestCache = new Map<
        string,
        Observable<SuggestDomainResponse>
    >();

    /**
     * Cache for searchOrganizations requests keyed by stringified normalized request
     */
    private readonly searchCache = new Map<
        string,
        Observable<SearchOrganizationsResponse>
    >();

    constructor(private readonly http: HttpClient) {}

    /**
     * Private path helpers
     */
    private organizationsPath(): string {
        return `${environment.apiURL}/organizations`;
    }

    private suggestPath(): string {
        return `${environment.apiURL}/organizations/suggest`;
    }

    private updatePath(id: string): string {
        return `${environment.apiURL}/organizations/${encodeURIComponent(id)}`;
    }

    private migratePath(id: string): string {
        return `${this.updatePath(id)}/migrate`;
    }

    /**
     * Search for organizations with optional filters
     *
     * @param request Search parameters
     * @returns Observable of organization array
     */
    searchOrganizations(
        request: SearchOrganizationsRequest = {},
    ): Observable<Organization[]> {
        return this.searchOrganizationsResponse(request).pipe(
            map((response) => response.organizations),
        );
    }

    searchOrganizationsResponse(
        request: SearchOrganizationsRequest = {},
    ): Observable<SearchOrganizationsResponse> {
        // Normalize request for cache key
        const cacheKey = this.normalizeSearchRequest(request);

        // Check cache
        const cached = this.searchCache.get(cacheKey);
        if (cached) {
            return cached;
        }

        // Build HTTP params
        let params = new HttpParams();
        if (request.text) {
            params = params.set("text", request.text.trim());
        }
        if (request.sector) {
            params = params.set("sector", request.sector);
        }
        if (request.levels && request.levels.length > 0) {
            // CSV format for levels
            params = params.set("levels", request.levels.join(","));
        }
        if (request.status) {
            const statusValue = Array.isArray(request.status)
                ? request.status.join(",")
                : request.status;
            params = params.set("status", statusValue);
        }
        if (request.domain) {
            params = params.set("domain", request.domain);
        }
        if (request.page !== undefined) {
            params = params.set("page", String(request.page));
        }
        if (request.limit !== undefined) {
            params = params.set("limit", String(request.limit));
        }

        // Make request with validation and caching
        const request$ = this.http
            .get<SearchOrganizationsResponse | Organization[]>(
                this.organizationsPath(),
                {
                    params,
                    withCredentials: true,
                },
            )
            .pipe(
                map((data): SearchOrganizationsResponse => {
                    if (Array.isArray(data)) {
                        const organizations =
                            OrganizationArraySchema.parse(data);
                        return {
                            organizations,
                            total: organizations.length,
                            page: 1,
                            limit: organizations.length,
                        };
                    }
                    return SearchOrganizationsResponseSchema.parse(data);
                }),
                catchError((error) => {
                    // Evict cache on error
                    this.searchCache.delete(cacheKey);
                    return throwError(() => error);
                }),
                shareReplay({ bufferSize: 1, refCount: false }),
            );

        // Store in cache
        this.searchCache.set(cacheKey, request$);

        return request$;
    }

    /**
     * Suggest organization based on email domain
     *
     * @param email User email address
     * @returns Observable of suggest response
     */
    suggestDomain(email: string): Observable<SuggestDomainResponse> {
        // Normalize email domain for cache key
        const cacheKey = this.normalizeEmailDomain(email);

        // Check cache
        const cached = this.suggestCache.get(cacheKey);
        if (cached) {
            return cached;
        }

        // Build HTTP params
        const params = new HttpParams().set("email", email);

        // Make request with validation and caching
        const request$ = this.http.get(this.suggestPath(), { params }).pipe(
            map(
                (data): SuggestDomainResponse =>
                    SuggestDomainResponseSchema.parse(data),
            ),
            catchError((error) => {
                // Evict cache on error
                this.suggestCache.delete(cacheKey);
                return throwError(() => error);
            }),
            shareReplay({ bufferSize: 1, refCount: false }),
        );

        // Store in cache
        this.suggestCache.set(cacheKey, request$);

        return request$;
    }

    /**
     * Create a new organization
     *
     * @param request Organization data
     * @returns Observable of created organization response
     */
    createOrganization(
        request: CreateOrganizationRequest,
    ): Observable<CreateOrganizationResponse> {
        return this.http
            .post<CreateOrganizationResponse>(this.organizationsPath(), request)
            .pipe(
                map((data) => CreateOrganizationResponseSchema.parse(data)),
                catchError((error) => throwError(() => error)),
            );
    }

    /**
     * Update an existing organization
     *
     * @param id Organization ID
     * @param request Update data
     * @returns Observable of updated organization response
     */
    updateOrganization(
        id: string,
        request: UpdateOrganizationRequest,
    ): Observable<UpdateOrganizationResponse> {
        return this.http
            .patch<UpdateOrganizationResponse>(this.updatePath(id), request)
            .pipe(
                map((data) => UpdateOrganizationResponseSchema.parse(data)),
                catchError((error) => throwError(() => error)),
            );
    }

    /**
     * Delete an existing organization.
     *
     * The backend signals success with HTTP 204 No Content.
     *
     * @param id Organization ID
     * @returns Observable that completes on success
     */
    deleteOrganization(id: string): Observable<void> {
        return this.http
            .delete<void>(this.updatePath(id), { observe: 'response' })
            .pipe(
                map(() => undefined),
                catchError(error => throwError(() => error))
            );
    }

    /**
     * Migrate all users from one organization to another.
     *
     * The backend signals success with HTTP 204 No Content.
     *
     * @param sourceOrganizationId Source organization ID
     * @param request Request containing target organization ID
     * @returns Observable that completes on success
     */
    migrateOrganizationUsers(
        sourceOrganizationId: string,
        request: MigrateOrganizationUsersRequest,
    ): Observable<void> {
        return this.http
            .post<void>(this.migratePath(sourceOrganizationId), request, {
                observe: "response",
            })
            .pipe(
                map(() => undefined),
                catchError((error) => throwError(() => error)),
            );
    }

    /**
     * Retrieve a single organization by ID
     *
     * @param id Organization ID
     * @returns Observable of organization
     */
    getOrganizationById(id: string): Observable<Organization> {
        return this.http
            .get<GetOrganizationByIdResponse>(this.updatePath(id))
            .pipe(
                map(
                    (data): Organization =>
                        GetOrganizationByIdResponseSchema.parse(data)
                            .organization,
                ),
                catchError((error) => throwError(() => error)),
            );
    }

    /**
     * Clear all caches (useful after create/update operations)
     */
    clearCache(): void {
        this.searchCache.clear();
        this.suggestCache.clear();
    }

    /**
     * Private helper: Normalize search request for stable cache key
     */
    private normalizeSearchRequest(
        request: SearchOrganizationsRequest,
    ): string {
        const normalized = {
            text: request.text?.trim() || undefined,
            sector: request.sector || undefined,
            levels:
                request.levels
                    ?.slice()
                    .sort((a, b) => a.localeCompare(b))
                    .join(",") || undefined,
            status: Array.isArray(request.status)
                ? request.status
                      .slice()
                      .sort((a, b) => a.localeCompare(b))
                      .join(",")
                : request.status,
            domain: request.domain || undefined,
            page: request.page,
            limit: request.limit,
        };
        return JSON.stringify(normalized);
    }

    /**
     * Private helper: Normalize email domain for cache key
     */
    private normalizeEmailDomain(email: string): string {
        const normalized = email.toLowerCase().trim();
        const atIndex = normalized.indexOf("@");
        if (atIndex === -1) {
            return normalized;
        }
        return normalized.substring(atIndex);
    }
}
